import { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import {
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { AxiosError } from 'axios';

import {
  createLienRecovery,
  getLienRecoveries,
  getLienRecoveryDocument,
  LienRecovery,
  updateLienRecovery,
  UpdateLienRecoveryRequest,
} from '../api/paymentsApi/deductionsApi';
import useSnackBar from '../hooks/useSnackBar';
import { ErrorData } from '../types';
import { lienRecoveryFormSchema } from '../helpers/formSchemas/formSchemas';
import { LIEN_RECOVERY_FORM_DEFAULT_VALUES } from '../helpers/formSchemas/formDefaultValues';
import { numberToUSDCurrency } from '../helpers/numberToUSDCurrency';
import { formatWithCurrencyMask } from '../helpers/formatWithCurrencyMask';

import FormCurrencyField from './formFields/FormCurrencyField';
import FormDateField from './formFields/FormDateField';
import FormPDFUploadField from './formFields/FormPDFUploadField';
import FormSelectField, { FormSelectOption } from './formFields/FormSelectField';
import FormTextField from './formFields/FormTextField';
import FormSubmitButton from './buttons/FormSubmitButton';
import FormCancelButton from './buttons/FormCancelButton';

const useStyles = makeStyles()((theme: Theme) => ({
  dialogContainer: {
    '& .MuiDialog-paper': {
      width: '100%',
      maxWidth: '560px',
      boxSizing: 'border-box',
      padding: '32px',
      gap: '24px',
      border: `1px solid ${theme.palette.secondary.dark}`,
      borderRadius: '16px',
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,

      '&::-webkit-scrollbar': {
        width: '5px',
        borderRadius: '20px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 20,
        backgroundColor: theme.palette.secondary.dark,
      },
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingRight: '40px',
  },
  dialogTitle: {
    fontFamily: 'Poppins',
    fontWeight: '600',
    fontSize: '24px',
    lineHeight: '28.8px',
    padding: 0,
  },
  subtitle: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '19.2px',
    color: theme.palette.text.disabled,
  },
  dialogContent: {
    padding: 0,
    overflowY: 'unset',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  fieldLabel: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16.8px',
    color: theme.palette.text.disabled,
  },
  fieldHint: {
    fontFamily: 'Inter',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '14.4px',
    color: theme.palette.text.disabled,
  },
  fieldError: {
    color: theme.palette.error.main,
  },
  selectField: {
    '&&': {
      margin: 0,
    },
  },
  dialogActions: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '416px',
    width: '100%',
    margin: '10px auto 0',
    gap: '16px',
    padding: 0,
  },
}));

export const LIEN_RECOVERY_REASON_OPTIONS: FormSelectOption[] = [
  { id: 'LIEN_REDUCED_AFTER_NEGOTIATION', label: 'Lien reduced after negotiation' },
  { id: 'DUPLICATE_PAYMENT', label: 'Duplicate payment' },
  { id: 'OVERPAYMENT', label: 'Overpayment' },
  { id: 'LIEN_WAIVED', label: 'Lien waived by provider' },
  { id: 'OTHER', label: 'Other' },
];

export interface LienRecoveryLien {
  // Deduction ID
  id: string;
  providerName: string;
  paidAmount: number;
  // Date the deduction was paid; the recovery cannot be received before it
  paidAt?: string | null;
}

interface LienRecoveryFormValues {
  amount: string;
  dateReceived: Moment | null;
  reason: string;
  note?: string;
  attachment: File | null;
}

interface LienRecoveryPopupProps {
  isOpen: boolean;
  lien: LienRecoveryLien;
  // When set, the popup edits this recovery instead of creating a new one
  recovery?: LienRecovery | null;
  onSuccess: () => void;
  onCancel: () => void;
}

// documentLink is a path or URL; show its last segment as the file name
const getDocumentFileName = (documentLink?: string | null) =>
  documentLink ? decodeURIComponent(documentLink.split('?')[0].split('/').pop() || '') : '';

const toAmountFieldValue = (amount: number) => formatWithCurrencyMask(amount.toFixed(2));

// Midnight UTC of the selected day, e.g. 2026-10-10T00:00:00.000Z
const toRecoveredDate = (date: Moment | null) =>
  moment.utc(moment(date).format('YYYY-MM-DD')).toISOString();

const LienRecoveryPopup = ({ isOpen, lien, recovery, onSuccess, onCancel }: LienRecoveryPopupProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();

  const isEditMode = Boolean(recovery);

  const [previouslyRecoveredAmount, setPreviouslyRecoveredAmount] = useState(0);
  const [remainingRecoverable, setRemainingRecoverable] = useState<number>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removeDocument, setRemoveDocument] = useState(false);
  const [vendorName, setVendorName] = useState<string>();
  const [deductionAmount, setDeductionAmount] = useState<number>();

  const paidAmount = deductionAmount ?? lien.paidAmount;

  // When editing, the recovery's own amount is available again on top of the remaining amount
  const maxRecoverableAmount =
    remainingRecoverable !== undefined
      ? remainingRecoverable + (recovery?.recoveredAmount ?? 0)
      : Math.max(paidAmount - previouslyRecoveredAmount, 0);

  // Keep a saved reason selectable even if it is not one of the predefined options
  const reasonOptions = useMemo(
    () =>
      recovery?.reason && !LIEN_RECOVERY_REASON_OPTIONS.some((option) => option.label === recovery.reason)
        ? [...LIEN_RECOVERY_REASON_OPTIONS, { id: recovery.reason, label: recovery.reason }]
        : LIEN_RECOVERY_REASON_OPTIONS,
    [recovery?.reason],
  );

  // Earliest selectable date received: the day the deduction was paid
  const minDateReceived = useMemo(
    () => (lien.paidAt ? moment(lien.paidAt).startOf('day') : undefined),
    [lien.paidAt],
  );

  const schema = useMemo(
    () => lienRecoveryFormSchema(maxRecoverableAmount, minDateReceived),
    [maxRecoverableAmount, minDateReceived],
  );

  const formMethods = useForm<LienRecoveryFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema) as never,
    defaultValues: LIEN_RECOVERY_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid, isDirty },
  } = formMethods;

  const amountError = errors.amount?.message;

  // Only the changed fields are sent; a new document goes as form-data, otherwise JSON
  const buildUpdatePayload = (
    existing: LienRecovery,
    values: LienRecoveryFormValues,
    amount: number,
    recoveredDate: string,
    reasonLabel: string,
  ) => {
    const changes: UpdateLienRecoveryRequest = {};
    if (amount !== existing.recoveredAmount) changes.recoveredAmount = amount;
    if (moment.utc(existing.recoveredDate).format('YYYY-MM-DD') !== moment.utc(recoveredDate).format('YYYY-MM-DD')) {
      changes.recoveredDate = recoveredDate;
    }
    if (reasonLabel !== existing.reason) changes.reason = reasonLabel;
    // An empty string is sent when the note is cleared
    const note = values.note?.trim() || '';
    if (note !== (existing.note || '')) changes.note = note;

    if (values.attachment) {
      const formData = new FormData();
      Object.entries(changes).forEach(([key, value]) => formData.append(key, String(value)));
      formData.append('document', values.attachment);
      return formData;
    }

    // A new document replaces the old one, so removeDocument is only sent without one
    if (removeDocument) changes.removeDocument = true;
    return changes;
  };

  const onSubmit = async (values: LienRecoveryFormValues) => {
    const note = values.note?.trim();
    const amount = Number(values.amount.replace(/,/g, ''));
    const recoveredDate = toRecoveredDate(values.dateReceived);
    const reasonLabel =
      reasonOptions.find((option) => option.id === values.reason)?.label || values.reason;

    setIsSubmitting(true);
    try {
      if (recovery) {
        await updateLienRecovery(
          lien.id,
          recovery.id,
          buildUpdatePayload(recovery, values, amount, recoveredDate, reasonLabel),
        );
      } else {
        const formData = new FormData();
        formData.append('recoveredAmount', String(amount));
        formData.append('recoveredDate', recoveredDate);
        formData.append('reason', reasonLabel);
        if (note) formData.append('note', note);
        if (values.attachment) formData.append('document', values.attachment);

        await createLienRecovery(lien.id, formData);
      }

      setAlert((prev) => ({
        ...prev,
        message: recovery ? 'Lien recovery updated successfully.' : 'Lien recovery saved successfully.',
        type: 'success',
      }));
      onSuccess();
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError?.response?.data?.message || 'Failed to save lien recovery.',
        type: 'error',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (recovery) {
      reset({
        amount: toAmountFieldValue(recovery.recoveredAmount),
        // Keep the saved calendar day regardless of the local timezone
        dateReceived: moment(moment.utc(recovery.recoveredDate).format('YYYY-MM-DD')),
        reason: reasonOptions.find((option) => option.label === recovery.reason)?.id || '',
        note: recovery.note || '',
        attachment: null,
      });
    } else {
      reset({ ...LIEN_RECOVERY_FORM_DEFAULT_VALUES, dateReceived: moment() });
    }
    setPreviouslyRecoveredAmount(0);
    setRemainingRecoverable(undefined);
    setRemoveDocument(false);
    setVendorName(undefined);
    setDeductionAmount(undefined);

    let isCancelled = false;
    getLienRecoveries(lien.id)
      .then((res) => {
        if (isCancelled) return;
        setVendorName(res.data.vendor?.name);
        setDeductionAmount(res.data.deductionAmount);
        // Exclude the recovery being edited from the previously recovered total
        setPreviouslyRecoveredAmount((res.data.totalRecovered ?? 0) - (recovery?.recoveredAmount ?? 0));
        setRemainingRecoverable(res.data.remainingRecoverable);

        // Pre-fill the amount returned with the full remaining recoverable amount
        if (!recovery && res.data.remainingRecoverable > 0) {
          setValue('amount', toAmountFieldValue(res.data.remainingRecoverable), {
            shouldValidate: true,
          });
        }
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, [isOpen, lien.id, recovery?.id]);

  const handleDownloadExistingDocument = () => {
    if (!recovery) return;

    getLienRecoveryDocument(lien.id, recovery.id)
      .then((res) => window.open(res.data.downloadUrl, '_blank'))
      .catch((error) => {
        const axiosError = error as AxiosError<ErrorData>;
        setAlert((prev) => ({
          ...prev,
          message: axiosError?.response?.data?.message || 'Failed to download document.',
          type: 'error',
        }));
      });
  };

  return (
    <Dialog className={classes.dialogContainer} open={isOpen} onClose={onCancel}>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        sx={(theme) => ({
          position: 'absolute',
          top: 24,
          right: 24,
          color: theme.palette.common.white,
        })}
      >
        <CloseIcon />
      </IconButton>
      <Box className={classes.header}>
        <DialogTitle className={classes.dialogTitle}>
          {isEditMode ? 'Edit recovery' : 'Mark lien as recovered'}
        </DialogTitle>
        <Typography className={classes.subtitle}>
          {vendorName || lien.providerName} · originally paid {numberToUSDCurrency(paidAmount)}
        </Typography>
      </Box>
      <DialogContent className={classes.dialogContent}>
        <FormProvider {...formMethods}>
          <form autoComplete="off" className={classes.form}>
            <Box className={classes.field}>
              <Typography className={classes.fieldLabel}>Amount returned</Typography>
              <FormCurrencyField name="amount" label="" helperText="" />
              <Typography className={clsx(classes.fieldHint, amountError && classes.fieldError)}>
                {amountError ||
                  `Paid ${numberToUSDCurrency(paidAmount)} · previously recovered ${numberToUSDCurrency(
                    previouslyRecoveredAmount,
                  )} · max ${numberToUSDCurrency(maxRecoverableAmount)}`}
              </Typography>
            </Box>
            <Box className={classes.field}>
              <Typography className={classes.fieldLabel}>Date received</Typography>
              <FormDateField name="dateReceived" label="" minDate={minDateReceived} maxDate={moment()} />
            </Box>
            <Box className={classes.field}>
              <Typography className={classes.fieldLabel}>Reason</Typography>
              <FormSelectField
                className={classes.selectField}
                name="reason"
                label=""
                width="100%"
                options={reasonOptions}
              />
            </Box>
            <Box className={classes.field}>
              <Typography className={classes.fieldLabel}>Note (optional)</Typography>
              <FormTextField name="note" label="" isMultiline isDescription  />
            </Box>
            <Box className={classes.field}>
              <Typography className={classes.fieldLabel}>Upload File</Typography>
              <FormPDFUploadField
                name="attachment"
                label="Upload File"
                allowedMimeTypes={['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/tiff']}
                allowedExtensions={['.pdf', '.jpg', '.jpeg', '.png', '.tif', '.tiff']}
                maxSizeMB={5}
                formatsLabel="PDF, JPG, PNG or TIFF"
                existingFileName={
                  removeDocument ? undefined : getDocumentFileName(recovery?.documentLink) || undefined
                }
                onDownload={handleDownloadExistingDocument}
                onDelete={() => setRemoveDocument(true)}
              />
              <Typography className={classes.fieldHint}>
                Attach the refund check image or remittance letter.
              </Typography>
            </Box>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions className={classes.dialogActions} disableSpacing>
        <FormSubmitButton
          disabled={!isValid || isSubmitting || (isEditMode && !isDirty && !removeDocument)}
          buttonText={isEditMode ? 'Save changes' : 'Save recovery'}
          onClick={handleSubmit(onSubmit)}
        />
        <FormCancelButton onClick={onCancel} disabled={isSubmitting} />
      </DialogActions>
    </Dialog>
  );
};

export default LienRecoveryPopup;
